import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Send, User, Mail, Phone } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useUserProfile } from "@/hooks/useUserProfile";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import Editor from "@/components/Editor";
import { buildClientLetterInsert } from "@/utils/clientWrites";
import { sanitizeLetterHtml, stripHtmlTags } from "@/utils/htmlUtils";

export default function WriteNewLetter() {
  const { userPlan, isAdmin, isLoading } = useUserProfile();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contentError, setContentError] = useState(false);
  const [formData, setFormData] = useState({
    recipientName: "",
    recipientEmail: "",
    recipientWhatsapp: "",
    content: "",
    deliveryDate: undefined as Date | undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const safeContent = sanitizeLetterHtml(formData.content);
    const isContentEmpty = stripHtmlTags(safeContent).trim().length === 0;
    setContentError(isContentEmpty);

    if (isContentEmpty || !formData.deliveryDate || !formData.recipientEmail.trim()) {
      toast({
        variant: "destructive",
        title: "Campos obrigatórios",
        description: "Por favor, preencha o email, a data e o conteúdo da carta."
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ variant: "destructive", title: "Erro de autenticação" });
        setIsSubmitting(false);
        return;
      }

      if (userPlan === 'premium' || isAdmin) {
        const letterInsert = buildClientLetterInsert({
          userId: session.user.id,
          email: formData.recipientEmail,
          whatsapp: formData.recipientWhatsapp,
          content: safeContent,
          deliveryDate: formData.deliveryDate.toISOString().split('T')[0],
        });
        const { error } = await supabase.from("letters").insert(letterInsert);

        if (error) throw error;

        toast({ title: "Carta criada com sucesso!" });
        navigate("/admin/cartas-agendadas");
      
      } else {
        const { data, error } = await supabase.functions.invoke('create-checkout', {
          body: JSON.stringify({
            content: safeContent,
            deliveryDate: formData.deliveryDate.toISOString().split('T')[0],
            email: formData.recipientEmail,
            whatsapp: formData.recipientWhatsapp,
            planType: 'email',
          }),
        });

        if (error) throw error;

        if (data?.url) {
          window.location.href = data.url;
        } else {
          throw new Error('URL do checkout não encontrada');
        }
      }
    } catch (error) {
      console.error("Erro ao processar carta:", error);
      toast({
        variant: "destructive",
        title: "Erro ao processar carta",
        description: "Ocorreu um erro. Tente novamente."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div>Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Escrever Nova Carta</h1>
        <p className="text-gray-600 mt-2">Crie uma carta personalizada para ser entregue no futuro</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              Destinatário
            </CardTitle>
            <CardDescription>
              Preencha os dados de quem receberá a carta.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="recipientEmail" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email do Destinatário *
                </Label>
                <Input
                  id="recipientEmail"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formData.recipientEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipientEmail: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipientWhatsapp" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  WhatsApp (opcional)
                </Label>
                <Input
                  id="recipientWhatsapp"
                  placeholder="(11) 99999-9999"
                  value={formData.recipientWhatsapp}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipientWhatsapp: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-2">
          <Label>Data de Entrega *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn("w-full justify-start text-left font-normal", !formData.deliveryDate && "text-muted-foreground")}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.deliveryDate ? format(formData.deliveryDate, "dd 'de' MMMM, yyyy", { locale: ptBR }) : <span>Selecione a data</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={formData.deliveryDate}
                onSelect={(date) => setFormData(prev => ({ ...prev, deliveryDate: date }))}
                disabled={(date) => date < new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label htmlFor="content">Mensagem da Carta *</Label>
          <Editor
            onContentChange={(content) => {
              setFormData(prev => ({ ...prev, content }));
              if (contentError) setContentError(false);
            }}
            hasError={contentError}
          />
        </div>
        
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          <Send className="mr-2 h-4 w-4" />
          {isSubmitting ? "Processando..." : (userPlan === 'basic' && !isAdmin ? 'Continuar para Pagamento' : 'Agendar Carta')}
        </Button>
      </form>
    </div>
  );
}